import React, { useEffect, useRef, useState } from "react";
import { ArrowUpRight, ShieldAlert, Settings, Globe2 } from "lucide-react";
import Icon from "@/components/Icon";
import CautionTape from "@/components/CautionTape";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import AdminPanel from "@/components/AdminPanel";
import SafeHtml from "@/components/SafeHtml";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { t } from "@/i18n";
import { fetchLinks, trackVisit, trackClick } from "@/api";
import { toast } from "sonner";

const hasArticleText = (html = "") =>
  html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .trim().length > 0;

const Landing = ({ langCtx, adminInitialOpen = false }) => {
  const { lang, setLang } = langCtx;
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminOpen, setAdminOpen] = useState(adminInitialOpen);
  const tapCountRef = useRef({ count: 0, last: 0 });

  const loadLinks = async () => {
    try {
      const data = await fetchLinks();
      setLinks(data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load links");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLinks();
    if (typeof window !== "undefined") {
      const key = "varenik_visit_logged";
      if (!sessionStorage.getItem(key)) {
        trackVisit();
        sessionStorage.setItem(key, "1");
      }
    }
  }, []);

  const onBrandTap = () => {
    const now = Date.now();
    const { count, last } = tapCountRef.current;
    const within = now - last < 800;
    const newCount = within ? count + 1 : 1;
    tapCountRef.current = { count: newCount, last: now };
    if (newCount >= 5) {
      tapCountRef.current = { count: 0, last: 0 };
      setAdminOpen(true);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "a") {
        e.preventDefault();
        setAdminOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleLinkClick = (link) => {
    trackClick(link.id);
  };

  return (
    <div className="relative min-h-screen overflow-x-hidden" data-testid="landing-root">
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher lang={lang} setLang={setLang} />
      </div>

      <button
        type="button"
        aria-label="admin"
        onClick={() => setAdminOpen(true)}
        data-testid="admin-trigger-gear"
        className="fixed bottom-4 left-4 z-50 p-2 rounded-full bg-black/50 border border-white/10 hover:border-[#FF1493] hover:text-[#FF1493] text-zinc-600 backdrop-blur transition-colors"
      >
        <Settings className="w-4 h-4" />
      </button>

      <main className="relative z-10 max-w-md mx-auto px-5 pt-20 pb-16 flex flex-col min-h-screen">
        <section className="text-center fade-up" data-testid="hero">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#FF1493]/40 rounded-full text-[10px] uppercase tracking-[0.3em] text-[#FF1493] font-mono-display mb-6">
            <ShieldAlert className="w-3 h-3" /> 18+ ONLY
          </div>
          <h1
            onClick={onBrandTap}
            data-testid="brand-title"
            className="font-display text-5xl sm:text-6xl font-semibold tracking-tight text-white leading-[1.05] cursor-pointer select-none"
          >
            {t(lang, "brand").split(" ")[0]}
            <span className="text-[#FF1493]"> 18+</span>
          </h1>
          <p className="mt-4 font-mono-display text-xs sm:text-sm uppercase tracking-[0.25em] text-zinc-400">
            {t(lang, "tagline")}
          </p>
          <p className="mt-2 font-mono-display text-[11px] text-zinc-500 max-w-xs mx-auto">
            {t(lang, "subtagline")}
          </p>
        </section>

        <div
          data-testid="vpn-notice"
          className="mt-6 flex items-start gap-3 p-3 rounded-md border border-[#FFE600]/40 bg-[#FFE600]/[0.04] text-[#FFE600] backdrop-blur-sm"
        >
          <Globe2 className="w-4 h-4 mt-0.5 shrink-0" />
          <p className="font-mono-display text-[11px] uppercase tracking-[0.18em] leading-relaxed">
            {t(lang, "vpnNotice")}
          </p>
        </div>

        <CautionTape text={t(lang, "cautionTape")} rotate="-rotate-2" />

        <section data-testid="links-stack">
          {loading && (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-16 rounded-lg bg-[#0c0c0c] border border-white/5 animate-pulse" />
              ))}
            </div>
          )}
          {!loading && links.length === 0 && (
            <p className="text-center text-zinc-500 text-sm">No links yet.</p>
          )}
          {!loading && links.length > 0 && (
            <Accordion type="single" collapsible className="flex flex-col gap-4 mt-2">
              {links.map((link, idx) => {
                const label = lang === "ru" ? link.label_ru : link.label_en;
                const article = lang === "ru" ? link.content_ru : link.content_en;
                return (
                  <AccordionItem
                    key={link.id}
                    value={link.id}
                    data-testid={`link-item-${idx}`}
                    className="btn-link group fade-up rounded-lg bg-[#0a0a0a] border border-white/10 backdrop-blur-md glow-pink overflow-hidden"
                    style={{ animationDelay: `${idx * 80}ms` }}
                  >
                    <AccordionTrigger
                      data-testid={`link-accordion-trigger-${idx}`}
                      className="p-5 hover:no-underline [&>svg]:text-[#FF1493] [&>svg]:w-5 [&>svg]:h-5"
                    >
                      <span className="flex items-center gap-4 relative z-10">
                        <span className="grid place-items-center w-10 h-10 rounded-md border border-white/10 bg-black group-hover:border-[#FF1493] group-hover:text-[#FF1493] text-white transition-colors">
                          <Icon name={link.icon} className="w-5 h-5" />
                        </span>
                        <span className="font-mono-display text-base sm:text-lg text-white font-medium uppercase tracking-wider text-left">
                          {label}
                        </span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent
                      data-testid={`link-accordion-content-${idx}`}
                      className="px-5 pb-5 text-zinc-300"
                    >
                      {hasArticleText(article) ? (
                        <SafeHtml
                          html={article}
                          testId={`link-article-${idx}`}
                          onLinkClick={() => handleLinkClick(link)}
                        />
                      ) : (
                        <p className="text-sm leading-6 text-zinc-500">{t(lang, "emptyArticle")}</p>
                      )}
                      {link.url && (
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => handleLinkClick(link)}
                          data-testid={`link-open-button-${idx}`}
                          className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#FF1493] text-black font-mono-display text-xs font-bold uppercase tracking-widest hover:bg-[#ff3aa3] transition-colors"
                        >
                          {t(lang, "openResource")}
                          <ArrowUpRight className="w-4 h-4" />
                        </a>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </section>

        <CautionTape text={t(lang, "cautionTape")} rotate="rotate-1" speed={45} />

        <footer className="mt-auto pt-8 text-center" data-testid="footer">
          <div
            className="inline-block stamp-pulse mb-4 px-4 py-2 border-2 border-[#FF1493] text-[#FF1493] font-display text-2xl"
            style={{ letterSpacing: "0.1em" }}
          >
            18+
          </div>
          <p className="text-[10px] font-mono-display text-zinc-500 uppercase tracking-[0.25em] max-w-xs mx-auto">
            {t(lang, "disclaimer")}
          </p>
          <p className="mt-3 text-[10px] font-mono-display text-zinc-600 uppercase tracking-widest">
            {t(lang, "footer")}
          </p>
        </footer>
      </main>

      <AdminPanel
        open={adminOpen}
        onOpenChange={setAdminOpen}
        lang={lang}
        onChanged={loadLinks}
      />
    </div>
  );
};

export default Landing;
